import { css } from 'aphrodite';
import * as React from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { Checkbox } from '../../../../../web/components/Checkbox';
import { ISelectOption } from '../../../../../web/components/DeprecatedSelect';
import { Dropdown } from '../../../../../web/components/Dropdown';
import { DisclosureIcon } from '../../../../../web/components/svgs/icons/DisclosureIcon';
import { FieldKey } from '../../../../viewModels/form';
import { IPhoneCallOutcome } from '../../../../viewModels/phonecall';
import { Group } from '../Group';
import { styleSheet } from './styles';
import { arrayOfLabels, checkAll, filterByChecked, prepareResponse, reduceChildren } from './utils';

interface IProps {
	closeOnClickInside?: boolean;
	defaultChecked?: boolean;
	defaultValues?: string[];
	isInline?: boolean;
	selectedOutcomes?: ISelectOption<FieldKey>[]; // For pill mode state management
	onChange?: (item: ISelectOption<FieldKey>[]) => void;
	className?: string;
}

export const OutcomeSelect: React.FC<IProps> = ({
	closeOnClickInside,
	defaultChecked = true,
	defaultValues,
	isInline,
	selectedOutcomes = [],
	onChange,
	className,
}) => {
	const userSession = useUserSession();
	const { logApiError } = useEventLogging('OutcomeSelect');
	const [outcomeOptions, setOutcomeOptions] = useState<ISelectOption<FieldKey>[]>([]);
	const [openDropdown, setOpenDropdown] = useState(false);
	const isInternalUpdateRef = useRef(false);

	// Update internal state when selectedOutcomes prop changes
	useEffect(() => {
		// Skip update if this was triggered by our own internal change
		if (isInternalUpdateRef.current) {
			isInternalUpdateRef.current = false;
			return;
		}

		if (selectedOutcomes && selectedOutcomes.length > 0) {
			// Update outcomeOptions based on selectedOutcomes
			setOutcomeOptions(prev => {
				const updated = prev.map(option => ({
					...option,
					checked: selectedOutcomes.findIndex(selected => selected.id === option.id) !== -1,
					children: option.children?.map(child => ({
						...child,
						checked: selectedOutcomes.findIndex(selected => selected.id === option.id) !== -1,
					})),
				}));
				return updated;
			});
		}
	}, [selectedOutcomes]);

	useEffect(() => {
		const loadOutcomes = async () => {
			try {
				const value = await userSession.webServiceHelper.callAsync<IPhoneCallOutcome[]>('aida/outcomes', 'POST', []);

				const options = value.map(prepareResponse);
				options.unshift({
					checked: true,
					dataContext: FieldKey.Unknown,
					id: 'all',
					name: 'all',
					text: 'All outcome types',
				});
				options?.map(checkAll(defaultChecked, defaultValues));
				setOutcomeOptions(options);
			} catch (error) {
				logApiError('LoadOutcomes-Error', error);

				// @ts-ignore
				toast.error(error.systemMessage ?? 'Unable to load outcomes');
			}
		};

		loadOutcomes();
	}, [defaultChecked, defaultValues, logApiError, userSession]);

	const handleCheckboxChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
		const isAll = target?.id === 'all';

		const checkSpecific = (parent: ISelectOption<FieldKey>) => {
			if (parent?.id === target.id) {
				parent.checked = target.checked;
			}
			parent?.children?.map((child: ISelectOption<FieldKey>) => {
				if (!Object.prototype.hasOwnProperty.call(Object(child), 'checked')) {
					child.checked = true;
				}
				if (parent?.id === target.id && target.checked) {
					child.checked = true;
				}
				if (parent?.id === target.id && !target.checked) {
					child.checked = false;
				}
				if (child?.id === target.id) {
					child.checked = target.checked;
					if (!target.checked) {
						parent.checked = false;
					}
				}
				return child;
			});
			const childrenCheckedSize = parent?.children?.filter(filterByChecked)?.length;
			if (childrenCheckedSize === parent?.children?.length) {
				parent.checked = true;
			}
			return parent;
		};

		const toggleAllOption = (check: boolean) => (parent: ISelectOption<FieldKey>) => {
			if (parent.id === 'all') {
				parent.checked = check;
			}
			return parent;
		};

		const outcomes = outcomeOptions.map(isAll ? checkAll(target.checked) : checkSpecific);

		const parentCheckedSize = outcomes?.filter(filterByChecked)?.length;

		if (parentCheckedSize === outcomes?.length) {
			outcomes.map(toggleAllOption(true));
		} else {
			outcomes.map(toggleAllOption(false));
		}
		if (closeOnClickInside) {
			setOpenDropdown(false);
		}
		isInternalUpdateRef.current = true;
		setOutcomeOptions(outcomes);

		onChange(outcomes);
	};

	const optionsChildrenOnly = outcomeOptions?.reduce(reduceChildren, []);
	const checkedOnly = optionsChildrenOnly?.filter(filterByChecked);
	const optionsLabelsOnly = checkedOnly.map(arrayOfLabels);
	const isAllSelected = checkedOnly?.length === optionsChildrenOnly?.length;
	const selectedLabel = optionsLabelsOnly?.length ? optionsLabelsOnly.join(', ') : 'None';
	const counterLabel = `Outcomes: ${isAllSelected ? 'All' : selectedLabel}`;

	const renderOutcomeList = (
		<>
			<Group vertical={true}>
				{outcomeOptions.map((parent: ISelectOption<FieldKey>, parentIndex) => {
					return (
						<Fragment key={parentIndex}>
							<Checkbox
								id={parent.id}
								type='large'
								onChange={handleCheckboxChange}
								checked={parent.checked}
								className={css(styleSheet.checkbox)}
							>
								<div className={css(styleSheet.checkboxText)}>{parent.text}</div>
							</Checkbox>
							{parent?.children?.map((child: ISelectOption<FieldKey>, childIndex) => {
								const childChecked = parent.checked || child.checked;
								return (
									<Checkbox
										key={childIndex}
										id={child.id}
										type='large'
										onChange={handleCheckboxChange}
										checked={childChecked}
										className={css([styleSheet.checkbox, styleSheet.childCheckbox])}
									>
										<div className={css(styleSheet.checkboxText)}>{child.label}</div>
									</Checkbox>
								);
							})}
						</Fragment>
					);
				})}
			</Group>
		</>
	);

	if (isInline) {
		return renderOutcomeList;
	}

	return (
		<Dropdown
			anchor={
				<span className={className || css(styleSheet.selectAnchor)} title={counterLabel}>
					{counterLabel} <DisclosureIcon className={css(styleSheet.selectAnchorIcon)} />
				</span>
			}
			className={`outcome-select ${css(styleSheet.multiSelect)}`}
			contentPositionY='bottom'
			isOpen={openDropdown}
			onOpenChanged={setOpenDropdown}
			openOnClick={true}
			contentInnerStyle={[styleSheet.selectDropdown, styleSheet.multiSelectDropdown]}
			contentInnerClassName='multiselect-dropdown'
		>
			{renderOutcomeList}
		</Dropdown>
	);
};
